import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import AvatarService from "@services/AvatarService";
import i18n from "@configs/i18n";
import {v4 as uuidv4} from "uuid";

const hasFeedbackPayload = (data) => {
  const hasJson =
    Array.isArray(data?.feedback_json) && data.feedback_json.length > 0;
  const hasText =
    typeof data?.feedback === "string"
      ? data.feedback.trim() !== ""
      : Boolean(data?.feedback);

  return hasJson || hasText;
};

export const getProxyList = createAsyncThunk("get/proxy_list", async () => {
  const response = await AvatarService.getProxyList();
  return response.data;
});

export const createScenarioSession = createAsyncThunk(
  "create/scenario_session",
  async ({level, scenario_id}, {getState}) => {
    const language = getState().common.settings.language;
    const response = await AvatarService.createScenarioSession({
      level,
      scenario_id,
      language,
    });

    return {
      ...response.data,
      instructorWelcome: i18n.t("page.chat.instructor_welcome", {lng: language}),
    };
  },
);

export const createProductSession = createAsyncThunk(
  "create/product_session",
  async ({level, product_id}) => {
    const response = await AvatarService.createProductSession({
      level,
      product_id,
    });

    return response.data;
  },
);

export const createCertificateSession = createAsyncThunk(
  "create/certificate_session",
  async ({level, scenario_id, course}, {getState}) => {
    const language = getState().common.settings.language;
    const response = await AvatarService.createCertificateSession({
      level,
      scenario_id,
      language,
      course,
    });

    return {
      ...response.data,
      instructorWelcome: i18n.t("page.chat.instructor_welcome", {lng: language}),
    };
  },
);

// Briefing rendering.
//
// The `[AVA:briefing]` console trace below is deliberate and permanent, not
// leftover debugging. The intro is assembled from a round trip whose failures
// are otherwise invisible — a rejected thunk, a null briefing, or a shape we
// decline to render all look identical on screen (a missing bubble), and one
// of them cost several days. Filter the browser console on "AVA:briefing" to
// see, for any session: whether the request was dispatched, what came back,
// which template matched, and how many bubbles were rendered.
//
// `patient_briefing` is per-course and its shape differs:
//   copilot  — a learner persona (user_name, role, market, phase, session_goal,
//              confidence_level), rendered as one interpolated sentence.
//   feedback — course, scenario_id, scenario, session_goal. `scenario` is
//              long-form prose, so it gets its own bubble with session_goal as
//              the lead-in; interpolating it mid-sentence reads badly.
// `briefing.course` is the machine key for template selection — Feedback
// deliberately omits the persona keys rather than sending blanks. Any other
// shape falls back to a generic "Label: value" list so new fields render
// instead of being silently dropped.
const COPILOT_BRIEFING_KEYS = [
  "user_name",
  "role",
  "market",
  "phase",
  "session_goal",
  "confidence_level",
];

// Machine keys — never rendered as briefing lines.
const BRIEFING_META_KEYS = ["course", "scenario_id"];

const humanizeBriefingKey = (key) =>
  key
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatBriefingValue = (value) =>
  Array.isArray(value) ? value.join(", ") : String(value);

const isPresent = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

// Identity of a briefing, used to detect a mid-session scenario swap.
export const briefingSignature = (briefing) =>
  briefing && typeof briefing === "object" ? JSON.stringify(briefing) : null;

// Returns the chat bubbles for a briefing: [] when there is nothing to show
// (null briefing, empty object, or only machine keys).
export const buildBriefingMessages = (briefing, language) => {
  const trace = (template, messages) => {
    // Which template matched is the whole game on the Feedback course: its
    // payload has none of Copilot's persona keys, so a wrong branch renders an
    // empty sentence or silently drops the scenario.
    console.info("[AVA:briefing] template", {
      course: briefing?.course ?? null,
      briefingKeys: briefing && typeof briefing === "object"
        ? Object.keys(briefing)
        : null,
      template,
      bubbles: messages.length,
    });

    return messages;
  };

  if (!briefing || typeof briefing !== "object") return trace("none", []);

  const t = (key, options = {}) => i18n.t(key, {lng: language, ...options});

  const entries = Object.entries(briefing).filter(
    ([key, value]) => !BRIEFING_META_KEYS.includes(key) && isPresent(value),
  );
  if (entries.length === 0) return trace("empty", []);

  const hasCopilotPersona = COPILOT_BRIEFING_KEYS.every((key) =>
    Object.prototype.hasOwnProperty.call(briefing, key),
  );

  if (briefing.course === "copilot" || hasCopilotPersona) {
    return trace("copilot", [
      {
        text: t("page.chat.instructor_session_briefing", {
          user_name: briefing.user_name,
          role: briefing.role,
          market: briefing.market,
          phase: briefing.phase,
          session_goal: briefing.session_goal,
          confidence_level: briefing.confidence_level,
        }),
        sender: "agent_instructor",
      },
    ]);
  }

  if (isPresent(briefing.scenario)) {
    const lead = isPresent(briefing.session_goal)
      ? t("page.chat.instructor_session_briefing_goal", {
          session_goal: briefing.session_goal,
        })
      : t("page.chat.instructor_session_briefing_title");

    return trace("feedback", [
      {text: lead, sender: "agent_instructor"},
      {text: String(briefing.scenario).trim(), sender: "agent_instructor"},
    ]);
  }

  const lines = entries.map(([key, value]) => {
    const label = t(`page.chat.briefing_fields.${key}`, {
      defaultValue: humanizeBriefingKey(key),
    });

    return `${label}: ${formatBriefingValue(value)}`;
  });

  // Reached when a payload matches no known course shape — worth noticing,
  // because it means the ML contract changed under us.
  return trace("generic", [
    {
      text: [t("page.chat.instructor_session_briefing_title"), ...lines].join(
        "\n",
      ),
      sender: "agent_instructor",
    },
  ]);
};

export const fetchPatientBriefing = createAsyncThunk(
  "fetch/patient_briefing",
  async ({id, sessionType, course}, {getState, dispatch}) => {
    const language = getState().common.settings.language;
    let briefing = null;
    // Any opening turn returned by the empty-content /conversation call. Today
    // the ML edge service answers it with "" — the scenario now travels in
    // `patient_briefing` instead — so this is normally a no-op.
    let opening = null;

    // Paired with the [AVA:briefing] logs in ChatHeygenGame: a session that
    // renders no scenario says whether the request was made, what it
    // answered, and how long it took. Kept deliberately — see the note above
    // buildBriefingMessages.
    const startedAt = Date.now();
    console.info("[AVA:briefing] request", {id, course});

    try {
      const response = await AvatarService.getCertificateBriefing({id});
      briefing = response.data?.patient_briefing;
      opening = response.data?.response;

      console.info("[AVA:briefing] response", {
        ms: Date.now() - startedAt,
        status: response.status,
        keys: Object.keys(response.data || {}),
        briefingKeys: briefing ? Object.keys(briefing) : null,
        diagnostic: response.data?.diagnostic,
      });

      // A null briefing means no scenario reaches the screen, so make the
      // reason visible rather than rendering an intro that is silently short
      // one bubble. `diagnostic` is the temporary field the controller adds.
      if (!briefing) {
        console.warn(
          "Patient briefing came back empty:",
          response.data?.diagnostic ?? response.data,
        );
      }
    } catch (err) {
      console.warn("Failed to fetch patient briefing:", {
        ms: Date.now() - startedAt,
        status: err?.response?.status,
        data: err?.response?.data ?? err?.message ?? err,
      });
    }

    const briefingMessages = buildBriefingMessages(briefing, language);
    console.info("[AVA:briefing] rendering", {bubbles: briefingMessages.length});

    briefingMessages.forEach((message) => {
      dispatch(chatSlice.actions.setMessages({
        messageId: uuidv4(),
        ...message,
      }));
    });

    // The briefing call is answered by the ML edge service and returns an empty
    // `response` (it consumes no dialogue turn). Kept as a pass-through in case
    // a course ever answers it with an opening turn.
    if (typeof opening === "string" && opening.trim() !== "") {
      dispatch(chatSlice.actions.setMessages({
        messageId: uuidv4(),
        text: opening,
        sender: "bot",
      }));
    }

    // Per-course intro copy; the briefing's own `course` wins over the one from
    // the route, and both fall back to the generic hint for sessions without a
    // course (real scenario/certification sessions).
    const hintCourse = briefing?.course || course;
    const startHint = i18n.t(
      [
        hintCourse ? `page.chat.instructor_start_hint_${hintCourse}` : null,
        "page.chat.instructor_start_hint",
      ].filter(Boolean),
      {lng: language},
    );
    setTimeout(() => {
      dispatch(chatSlice.actions.setMessages({
        messageId: uuidv4(),
        text: startHint,
        sender: "agent_instructor",
      }));
    }, 5000);

    return {patient_briefing: briefing};
  },
  {
    // `initSession` can fire twice, which would duplicate the intro bubbles.
    // The pending reducer records the session id synchronously, so a second
    // dispatch is dropped before it reaches the network.
    //
    // The slice is mounted at state.ml.chat (see redux/store.js) — reading
    // state.chat here threw inside `condition`, which RTK turns into a
    // rejected thunk, so the briefing was never fetched at all.
    condition: ({id}, {getState}) =>
      getState().ml?.chat?.briefing_session_id !== id,
  },
);

export const sendMessage = createAsyncThunk(
  "send/message",
  async ({id, msg}) => {
    const response = await AvatarService.sendMessage({id, msg});

    return response.data;
  },
);

export const sendCertificateMessage = createAsyncThunk(
  "send/certificate_message",
  async ({id, msg}, {getState, rejectWithValue}) => {
    try {
      const response = await AvatarService.sendCertificateMessage({id, msg});

      // Language rides along so the reducer can re-render a swapped briefing
      // (see the fulfilled case) without reaching into another slice.
      return {
        ...response.data,
        language: getState().common.settings.language,
      };
    } catch (error) {
      // Surface the server error body (see the controller's rescue_from) so the
      // failure cause is visible in the console instead of a generic reject.
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const getQuestion = createAsyncThunk("get/question", async ({id}) => {
  const response = await AvatarService.getQuestion({id});

  return response.data;
});

export const sendAnswer = createAsyncThunk(
  "send/answer",
  async ({id, answer_id, answer}) => {
    const response = await AvatarService.sendAnswer({id, answer_id, answer});

    return response.data;
  },
);

export const getScenarioFeedback = createAsyncThunk(
  "session/scenario-feedback",
  async (id) => {
    let attempts = 0;
    const maxAttempts = 9;

    while (attempts < maxAttempts) {
      const response = await AvatarService.getScenarioFeedback(id);
      const {delay} = response.data;

      if (hasFeedbackPayload(response.data)) {
        return response.data;
      } else if (delay) {
        await new Promise((resolve) => setTimeout(resolve, delay * 1000));
        attempts++;
      } else {
        return response.data;
      }
    }

    const finalResponse = await AvatarService.getScenarioFeedback(id);

    if (hasFeedbackPayload(finalResponse?.data)) {
      return finalResponse.data;
    } else {
      return {feedback: "generated"};
    }
  },
);

export const getCertificateFeedback = createAsyncThunk(
  "session/certificate-feedback",
  async (id) => {
    let attempts = 0;
    const maxAttempts = 9;

    while (attempts < maxAttempts) {
      const response = await AvatarService.getCertificateFeedback(id);
      const {delay} = response.data;

      if (hasFeedbackPayload(response.data)) {
        return response.data;
      } else if (delay) {
        await new Promise((resolve) => setTimeout(resolve, delay * 1000));
        attempts++;
      } else {
        return response.data;
      }
    }

    const finalResponse = await AvatarService.getCertificateFeedback(id);

    if (hasFeedbackPayload(finalResponse?.data)) {
      return finalResponse.data;
    } else {
      return {feedback: "generated"};
    }
  },
);

export const getProductFeedback = createAsyncThunk(
  "session/product-feedback",
  async (id) => {
    let attempts = 0;
    const maxAttempts = 9;

    while (attempts < maxAttempts) {
      const response = await AvatarService.getProductFeedback(id);
      const {delay} = response.data;

      if (hasFeedbackPayload(response.data)) {
        return response.data;
      } else if (delay) {
        await new Promise((resolve) => setTimeout(resolve, delay * 1000));
        attempts++;
      } else {
        return response.data;
      }
    }

    const finalResponse = await AvatarService.getProductFeedback(id);
    return finalResponse.data;
  },
);

export const getRecordToken = createAsyncThunk("get/record-token", async () => {
  const response = await AvatarService.getRecordToken();

  return response.data;
});

export const getAvatarToken = createAsyncThunk(
  "get/avatar-token",
  async (payload = {}) => {
    const response = await AvatarService.getAvatarToken(payload);

    return response.data;
  },
);

export const getAkoolToken = createAsyncThunk("get/akool-token", async () => {
  const response = await AvatarService.getAkoolToken();

  return response.data;
});

const initialState = {
  avatarId: "",
  voiceId: "",
  scenario_session_id: null,
  product_session_id: null,
  certificate_session_id: null,
  sessionState: "",
  userMessage: "",
  botMessage: "",
  messages: [],
  patient_briefing: null,
  // Signature of the briefing already on screen; a change means the ML service
  // swapped the scenario and we must render it again.
  patient_briefing_signature: null,
  // Session whose briefing has been requested, so a re-entrant init is a no-op.
  briefing_session_id: null,
  // "idle" | "loading" | "ready". While loading, the composer is held closed so
  // the scenario is on screen before the user's first message — otherwise a
  // fast typist beats the briefing and the intro lands mid-conversation.
  briefingStatus: "idle",
  score: null,
  questions: null,
  answer_id: null,
  isRecording: false,
  recordToken: null,
  recordHost: null,
  volume: 0.2,
  start_time: null,
  schedule_intent: null,
  error: "",
  serverStatus: "",
  feedbackStatus: {state: "", session: ""},
  feedback: "",
  feedback_json: null,
  feedback_score: null,
  max_feedback_score: null,
  certified: false,
  proxyList: {},
  successful: false,
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setScheduleIntent(state, action) {
      state.schedule_intent = action.payload;
    },
    setAvatarId(state, action) {
      state.avatarId = action.payload;
    },
    setVoiceId(state, action) {
      state.voiceId = action.payload;
    },
    setScore(state, action) {
      state.score = action.payload;
    },
    setScenarioSessionId(state, action) {
      state.scenario_session_id = action.payload;
    },
    setProductSessionId(state, action) {
      state.product_session_id = action.payload;
    },
    setCertificateSessionId(state, action) {
      state.certificate_session_id = action.payload;
    },
    setIsRecording(state, action) {
      state.isRecording = action.payload;
    },
    setAnswerId(state, action) {
      state.answer_id = action.payload;
    },
    setQuestions(state, action) {
      state.questions = action.payload;
    },
    setUserMessage(state, action) {
      state.userMessage = action.payload;
    },
    setBotMessage(state, action) {
      state.botMessage = action.payload;
    },
    setMessages(state, action) {
      state.messages.push(action.payload);
    },
    setVolume(state, action) {
      state.volume = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    setFeedback(state, action) {
      state.feedback = action.payload;
    },
    setFeedbackScore(state, action) {
      state.feedback_score = action.payload;
    },
    setFeedbackJson(state, action) {
      state.feedback_json = action.payload;
    },
    setFeedbackStatus(state, action) {
      state.feedbackStatus = {
        state: action.payload.state,
        session: action.payload.session,
      };
    },
    setServerStatus(state, action) {
      state.serverStatus = action.payload;
    },
    setStartTime(state, action) {
      state.start_time = action.payload;
    },
    setProxyList(state, action) {
      state.proxyList = action.payload;
    },
    // Releases the composer even though the briefing has not landed, so a slow
    // or hung ML call cannot lock the user out of their own session.
    setBriefingReady(state) {
      state.briefingStatus = "ready";
    },
    clearMessages(state) {
      state.messages = [];
    },
    clearChatState(state) {
      state.schedule_intent = null;
      state.userMessage = "";
      state.botMessage = "";
      state.answer_id = null;
      state.score = null;
      state.messages = [];
      state.patient_briefing = null;
      state.patient_briefing_signature = null;
      state.briefing_session_id = null;
      state.briefingStatus = "idle";
      state.sessionState = "";
      state.questions = null;
      state.recordToken = null;
      state.isRecording = false;
      state.recordHost = null;
      state.start_time = null;
      state.feedback = "";
      state.error = "";
      state.feedback_score = null;
      state.feedback_json = null;
      state.max_feedback_score = null;
      state.certified = false;
      state.proxyList = {};
      state.successful = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // getProxyList
      .addCase(getProxyList.pending, (state) => {
        state.proxyList = {};
        state.serverStatus = "loading";
      })
      .addCase(getProxyList.fulfilled, (state, action) => {
        state.proxyList = action?.payload;
        state.error = "";
        state.serverStatus = "success";
      })
      .addCase(getProxyList.rejected, (state) => {
        state.error = "Don`t get proxy list!";
        state.serverStatus = "error";
      })

      // createScenarioSession
      .addCase(createScenarioSession.pending, (state) => {
        state.serverStatus = "loading";
      })
      .addCase(createScenarioSession.fulfilled, (state, action) => {
        state.scenario_session_id = action?.payload?.id;
        state.error = "";
        state.serverStatus = "success";
        state.messages.push({
          messageId: uuidv4(),
          text: action.payload.instructorWelcome,
          sender: "agent_instructor",
        });
      })
      .addCase(createScenarioSession.rejected, (state) => {
        state.error = "Don`t create scenario session!";
        state.serverStatus = "error";
      })

      // createProductSession
      .addCase(createProductSession.pending, (state) => {
        state.serverStatus = "loading";
      })
      .addCase(createProductSession.fulfilled, (state, action) => {
        state.product_session_id = action?.payload?.id;
        state.questions = action?.payload?.questions_count;
        state.error = "";
        state.serverStatus = "success";
      })
      .addCase(createProductSession.rejected, (state) => {
        state.error = "Don`t create product session!";
        state.serverStatus = "error";
      })

      // createCertificateSession
      .addCase(createCertificateSession.pending, (state) => {
        state.serverStatus = "loading";
      })
      .addCase(createCertificateSession.fulfilled, (state, action) => {
        state.certificate_session_id = action?.payload?.id;
        state.error = "";
        state.serverStatus = "success";
        state.messages.push({
          messageId: uuidv4(),
          text: action.payload.instructorWelcome,
          sender: "agent_instructor",
        });
      })
      .addCase(createCertificateSession.rejected, (state) => {
        state.error = "Don`t create certificate session!";
        state.serverStatus = "error";
      })

      // fetchPatientBriefing — the thunk renders the bubbles itself; record the
      // briefing so a later turn can tell whether it changed.
      .addCase(fetchPatientBriefing.pending, (state, action) => {
        state.briefing_session_id = action.meta.arg?.id ?? null;
        state.briefingStatus = "loading";
      })
      .addCase(fetchPatientBriefing.fulfilled, (state, action) => {
        state.patient_briefing = action.payload?.patient_briefing ?? null;
        state.patient_briefing_signature = briefingSignature(
          action.payload?.patient_briefing,
        );
        state.briefingStatus = "ready";
      })
      .addCase(fetchPatientBriefing.rejected, (state) => {
        state.briefingStatus = "ready";
      })

      // getScenarioFeedback
      .addCase(getScenarioFeedback.pending, (state) => {
        state.serverStatus = "loading";
        state.feedbackStatus = {state: "pending", session: "scenario"};
      })
      .addCase(getScenarioFeedback.fulfilled, (state, action) => {
        state.feedback = action.payload.feedback || "";
        state.feedback_score = action?.payload?.feedback_score;
        state.feedback_json = action?.payload?.feedback_json;
        state.max_feedback_score = action?.payload?.max_possible_score;
        state.error = "";
        state.serverStatus = hasFeedbackPayload(action.payload)
          ? "success"
          : "loading";
        state.feedbackStatus = {state: "completed", session: "scenario"};
        state.successful = action?.payload?.successful;
      })
      .addCase(getScenarioFeedback.rejected, (state) => {
        state.error = "Don`t get scenario feedback!";
        state.serverStatus = "error";
        state.feedbackStatus = {state: "error", session: "scenario"};
      })

      // getProductFeedback
      .addCase(getProductFeedback.pending, (state) => {
        state.serverStatus = "loading";
        state.feedbackStatus = {state: "pending", session: "product"};
      })
      .addCase(getProductFeedback.fulfilled, (state, action) => {
        state.feedback = action.payload.feedback || "";
        state.feedback_json = action?.payload?.feedback_json;
        state.error = "";
        state.serverStatus = hasFeedbackPayload(action.payload)
          ? "success"
          : "loading";
        state.feedbackStatus = {state: "completed", session: "product"};
      })
      .addCase(getProductFeedback.rejected, (state) => {
        state.error = "Don`t get product feedback!";
        state.serverStatus = "error";
        state.feedbackStatus = {state: "error", session: "product"};
      })

      // getCertificateFeedback
      .addCase(getCertificateFeedback.pending, (state) => {
        state.serverStatus = "loading";
        state.feedbackStatus = {state: "pending", session: "certification"};
      })
      .addCase(getCertificateFeedback.fulfilled, (state, action) => {
        state.feedback = action.payload.feedback || "";
        state.feedback_score = action?.payload?.feedback_score;
        state.feedback_json = action?.payload?.feedback_json;
        state.max_feedback_score = action?.payload?.max_possible_score;
        state.certified = action?.payload?.certified;
        state.error = "";
        state.serverStatus = hasFeedbackPayload(action.payload)
          ? "success"
          : "loading";
        state.feedbackStatus = {state: "completed", session: "certification"};
      })
      .addCase(getCertificateFeedback.rejected, (state) => {
        state.error = "Don`t get certificate feedback!";
        state.serverStatus = "error";
        state.feedbackStatus = {state: "error", session: "certification"};
      })

      // sendMessage
      .addCase(sendMessage.pending, (state) => {
        state.serverStatus = "loading";
        state.sessionState = "";
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.botMessage = action.payload.ai;
        state.score = action.payload.score;
        state.sessionState = action.payload.state;
        state.error = "";
        state.serverStatus = "success";
        state.schedule_intent = action.payload.schedule_intent;

        if (action.payload) {
          const bot = {
            messageId: uuidv4(),
            text: state.botMessage,
            sender: "bot",
          };

          state.messages.push(bot);

          if (action.payload.agent_instructor) {
            const agent3 = {
              messageId: uuidv4(),
              text: action.payload.agent_instructor,
              sender: "agent_instructor",
            };
            state.messages.push(agent3);
          }
        }
      })
      .addCase(sendMessage.rejected, (state) => {
        state.error = "Don`t send message!";
        state.sessionState = "";
        state.serverStatus = "error";
      })

      // sendCertificateMessage
      .addCase(sendCertificateMessage.pending, (state) => {
        state.serverStatus = "loading";
        state.sessionState = "";
      })
      .addCase(sendCertificateMessage.fulfilled, (state, action) => {
        state.botMessage = action.payload.ai;
        state.score = action.payload.score;
        state.sessionState = action.payload.state;
        state.error = "";
        state.serverStatus = "success";
        state.schedule_intent = action.payload.schedule_intent;

        if (action.payload) {
          const bot = {
            messageId: uuidv4(),
            text: state.botMessage,
            sender: "bot",
          };

          state.messages.push(bot);

          if (action.payload.agent_instructor) {
            const agent3 = {
              messageId: uuidv4(),
              text: action.payload.agent_instructor,
              sender: "agent_instructor",
            };
            state.messages.push(agent3);
          }

          // The briefing comes back on every turn and can legitimately change
          // mid-session (the user may propose a different situation, which
          // swaps the scenario). Re-render it whenever it differs, so the
          // scenario bubble never goes stale.
          const nextBriefing = action.payload.patient_briefing;
          const nextSignature = briefingSignature(nextBriefing);

          console.info("[AVA:briefing] turn", {
            course: nextBriefing?.course ?? null,
            scenarioId: nextBriefing?.scenario_id ?? null,
            present: Boolean(nextBriefing),
            changed: nextSignature !== state.patient_briefing_signature,
            // True on the first turn means the intro never rendered it and we
            // are about to do the intro's job late — the exact failure that
            // hid the rejected briefing thunk for days.
            firstEver: state.patient_briefing_signature === null,
          });

          if (nextSignature !== state.patient_briefing_signature) {
            state.patient_briefing = nextBriefing ?? null;
            state.patient_briefing_signature = nextSignature;

            buildBriefingMessages(nextBriefing, action.payload.language).forEach(
              (message) => {
                state.messages.push({messageId: uuidv4(), ...message});
              },
            );
          }
        }
      })
      .addCase(sendCertificateMessage.rejected, (state) => {
        state.error = "Don`t send message!";
        state.sessionState = "";
        state.serverStatus = "error";
      })

      // getQuestion
      .addCase(getQuestion.pending, (state) => {
        state.serverStatus = "loading";
      })
      .addCase(getQuestion.fulfilled, (state, action) => {
        state.botMessage = action.payload.text;
        state.answer_id = action.payload.answer_id;
        state.error = "";
        state.serverStatus = "success";

        if (action.payload) {
          const bot = {
            messageId: uuidv4(),
            text: state.botMessage,
            sender: "bot",
          };

          state.messages.push(bot);
          state.questions = state.questions - 1;
        }
      })
      .addCase(getQuestion.rejected, (state) => {
        state.error = "Don`t get question!";
        state.serverStatus = "error";
      })

      // sendAnswer
      .addCase(sendAnswer.pending, (state) => {
        state.serverStatus = "loading";
      })
      .addCase(sendAnswer.fulfilled, (state, action) => {
        state.score = action.payload.score;
        state.error = "";
        state.serverStatus = "success";
      })
      .addCase(sendAnswer.rejected, (state) => {
        state.error = "Don`t send answer!";
        state.serverStatus = "error";
      })

      // getRecordToken
      .addCase(getRecordToken.pending, (state) => {
        state.recordToken = null;
        state.recordHost = null;
      })
      .addCase(getRecordToken.fulfilled, (state, action) => {
        state.recordToken = action.payload.access_token;
        state.recordHost = action.payload.host;
        state.error = "";
      })
      .addCase(getRecordToken.rejected, (state) => {
        state.error = "Don`t get record token!";
        state.recordToken = null;
        state.recordHost = null;
      });
  },
});

export const {
  setScore,
  setCertificateSessionId,
  setScenarioSessionId,
  setProductSessionId,
  setQuestions,
  setBotMessage,
  setUserMessage,
  setMessages,
  setVolume,
  setError,
  setServerStatus,
  clearChatState,
  setIsRecording,
  setAnswerId,
  setAvatarId,
  setVoiceId,
  clearMessages,
  setFeedback,
  setStartTime,
  setScheduleIntent,
  setFeedbackStatus,
  setFeedbackJson,
  setBriefingReady,
} = chatSlice.actions;
export default chatSlice.reducer;
